import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import axios from "axios";
import "../styles/ChatComponent.css";

let socket = null;

const ChatComponent = ({ user: initialUser, room }) => {
  if (!socket) {
    socket = io("https://drp-backend-1y8r.onrender.com", {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
  }

  const [user, setUser] = useState(() => {
    try {
      let guestId = localStorage.getItem("guestId");
      if (!initialUser?._id && !guestId) {
        guestId = `guest_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        localStorage.setItem("guestId", guestId);
      }
      return {
        ...initialUser,
        _id: initialUser?._id || guestId,
        name: initialUser?.name || null,
      };
    } catch (error) {
      console.error("LocalStorage error:", error);
      // Fallback if localStorage fails
      const fallbackId = `guest_${Date.now()}_${Math.floor(
        Math.random() * 1000
      )}`;
      return {
        ...initialUser,
        _id: initialUser?._id || fallbackId,
        name: initialUser?.name || null,
      };
    }
  });

  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Component visibility control
  const messagesEndRef = useRef(null);

  // Show component immediately
  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (!room || !emailSubmitted) return;

    setIsLoading(true);

    // Join room via socket
    socket.emit("joinRoom", { room });

    // Fetch chat history with timeout
    const fetchTimeout = setTimeout(() => {
      setIsLoading(false);
      // Show welcome message even if API fails
      setMessages([
        {
          _id: `welcome_${Date.now()}`,
          sender: { name: "SAAR DRP Support", _id: "bot" },
          message:
            "👋 Hello! Thank you for contacting SAAR DRP Support. How can we help you today?",
          createdAt: new Date(),
          isBot: true,
        },
      ]);
    }, 5000); // 5 second timeout

    axios
      .get(`/api/v1/chat/${room}`)
      .then((res) => {
        clearTimeout(fetchTimeout);
        setIsLoading(false);
        const history = res.data || [];
        if (history.length > 0) {
          setMessages(history);
        } else {
          setMessages([
            {
              _id: `welcome_${Date.now()}`,
              sender: { name: "SAAR DRP Support", _id: "bot" },
              message:
                "👋 Hello! Thank you for contacting SAAR DRP Support. How can we help you today?",
              createdAt: new Date(),
              isBot: true,
            },
          ]);
        }
      })
      .catch((error) => {
        console.error("Failed to load chat history:", error);
        clearTimeout(fetchTimeout);
        setIsLoading(false);
        // Still show welcome message on error
        setMessages([
          {
            _id: `welcome_${Date.now()}`,
            sender: { name: "SAAR DRP Support", _id: "bot" },
            message:
              "👋 Hello! Thank you for contacting SAAR DRP Support. How can we help you today?",
            createdAt: new Date(),
            isBot: true,
          },
        ]);
      });

    // Socket message listener
    const handleReceiveMessage = (newMessage) => {
      if (!newMessage.message || newMessage.message.trim() === "") return;
      setIsBotTyping(false);
      setMessages((prev) => {
        const exists = prev.find(
          (m) =>
            m._id === newMessage._id ||
            (m.message === newMessage.message &&
              m.createdAt === newMessage.createdAt)
        );
        return exists ? prev : [...prev, newMessage];
      });
    };

    socket.on("receiveMessage", handleReceiveMessage);

    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
      clearTimeout(fetchTimeout);
    };
  }, [room, emailSubmitted]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  const sendMessage = () => {
    const textToSend = message.trim();
    if (!textToSend) return;

    const payload = {
      _id: `temp_${Date.now()}`,
      message: textToSend,
      sender: { _id: user._id, name: user.name || email.split("@")[0] },
      guestEmail: email,
      guestName: email.split("@")[0],
      room,
      createdAt: new Date().toISOString(),
    };

    // Update UI immediately
    setMessages((prev) => [...prev, payload]);
    setMessage("");

    // Bot auto-reply logic
    const userMessageCount = messages.filter(
      (m) => !m.isBot && !m.isAdminMessage
    ).length;

    if (userMessageCount === 0) {
      setTimeout(() => {
        setIsBotTyping(true);
        setTimeout(() => {
          const autoReply = {
            _id: `auto_${Date.now()}`,
            sender: { name: "SAAR DRP Support", _id: "bot" },
            message:
              "🚀 I’ve notified our support team. Please share a screenshot or brief details of the issue so we can assist you further.",
            createdAt: new Date(),
            isBot: true,
          };
          setMessages((prev) => [...prev, autoReply]);
          setIsBotTyping(false);
        }, 2500);
      }, 800);
    }

    // Send to server (non-blocking)
    socket.emit("sendMessage", payload);
    axios.post("/api/v1/chat/message", payload).catch((error) => {
      console.error("Failed to send message:", error);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleEmailSubmit = () => {
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address");
      return;
    }
    setEmailSubmitted(true);
    socket.emit("registerEmail", { room, email });
    axios
      .post("/api/v1/chat/register-email", { room, email })
      .catch((error) => {
        console.error("Failed to register email:", error);
      });
  };

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>SAARDRP Support Chat</h3>
        <span className="status-indicator">Online</span>
      </div>

      {!emailSubmitted ? (
        <div className="email-step">
          <div className="bot-icon">🤖</div>
          <p>To start a secure session, please verify your email.</p>
          <input
            type="email"
            placeholder="Enter your email..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleEmailSubmit()}
          />
          <button onClick={handleEmailSubmit}>Start Conversation</button>
        </div>
      ) : (
        <>
          <div className="messages-container">
            {isLoading && messages.length === 0 ? (
              <div className="loading-indicator">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p>Loading chat...</p>
              </div>
            ) : (
              messages.map((msg, index) => {
                const isAdmin = msg.isBot || msg.isAdminMessage;
                const name = isAdmin
                  ? "SAAR DRP Admin Head"
                  : msg.sender?.name || msg.guestName || "Guest";
                const isMe =
                  !isAdmin &&
                  (msg.sender?._id === user._id ||
                    (msg.guestEmail === email && email !== ""));

                return (
                  <div
                    key={msg._id || index}
                    className={
                      isAdmin
                        ? "other-message admin-reply"
                        : isMe
                        ? "my-message"
                        : "other-message"
                    }
                  >
                    <strong className="sender-name">{name}</strong>
                    <div className="message-text">{msg.message}</div>
                    <div className="message-time">
                      {new Date(msg.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                );
              })
            )}

            {isBotTyping && (
              <div className="other-message bot-typing">
                <strong className="sender-name">SAAR DRP Support</strong>
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Describe your issue..."
              autoComplete="off"
            />
            <button onClick={sendMessage} disabled={!message.trim()}>
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path
                  fill="currentColor"
                  d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"
                />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatComponent;
